import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, AlertTriangle } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

const INT_EXT_OPTIONS = ["INT", "EXT", "INT/EXT"];
const TIME_OF_DAY_OPTIONS = [
    "DAY",
    "NIGHT",
    "DAWN",
    "DUSK",
    "MORNING",
    "EVENING",
    "CONTINUOUS",
    "LATER",
];

/**
 * Owner-only Add Scene dialog. Always requires scene_number + heading.
 * Duplicate scene_number blocked client-side (case-insensitive, trimmed) —
 * matches backend 409 "Scene number already exists." contract.
 *
 * Props:
 *   projectId: string
 *   existingScenes: [{ scene_number, ... }] used for duplicate detection
 *   open, onClose, onCreated(newScene)
 */
export default function AddSceneDialog({ projectId, existingScenes = [], open, onClose, onCreated }) {
    const apiBase = useApiBase();
    const [form, setForm] = useState({
        scene_number: "",
        episode: "",
        heading: "",
        int_ext: "",
        time_of_day: "",
        description: "",
        story_time: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setForm({
            scene_number: "",
            episode: "",
            heading: "",
            int_ext: "",
            time_of_day: "",
            description: "",
            story_time: "",
        });
        setError("");
    }, [open]);

    const trimmedNumber = form.scene_number.trim();
    const trimmedEpisode = form.episode.trim();
    // Duplicate check is scoped to the same episode (or to the
    // global film namespace when episode is blank) to mirror the
    // backend's per-episode uniqueness rule.
    const existingNumbersInEpisode = useMemo(() => {
        const ep = trimmedEpisode || null;
        return new Set(
            (existingScenes || [])
                .filter((s) => (s.episode || null) === ep)
                .map((s) => (s.scene_number || "").trim().toUpperCase())
                .filter(Boolean),
        );
    }, [existingScenes, trimmedEpisode]);
    const isDuplicate =
        trimmedNumber.length > 0 &&
        existingNumbersInEpisode.has(trimmedNumber.toUpperCase());

    const canSave =
        trimmedNumber.length > 0 &&
        form.heading.trim().length > 0 &&
        !isDuplicate &&
        !saving;

    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        setError("");
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/scenes`, {
                scene_number: trimmedNumber,
                episode: form.episode.trim() || null,
                heading: form.heading.trim(),
                int_ext: form.int_ext || null,
                time_of_day: form.time_of_day || null,
                description: form.description.trim() || null,
                story_time: form.story_time.trim() || null,
            });
            toast.success(`Scene ${data.scene_number} added`);
            onCreated?.(data);
            onClose?.();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (!v && !saving ? onClose?.() : null)}>
            <DialogContent
                data-testid="add-scene-dialog"
                className="max-w-md w-full bg-card border-white/10 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-bold text-white flex items-center gap-2">
                        <Plus size={20} />
                        Add scene
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Scene number becomes permanent once saved.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-1">
                    <div>
                        <Label
                            htmlFor="add-scene-episode"
                            className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Episode <span className="lowercase tracking-normal text-neutral-600">(optional · TV)</span>
                        </Label>
                        <Input
                            id="add-scene-episode"
                            data-testid="add-scene-episode-input"
                            value={form.episode}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    episode: e.target.value,
                                }))
                            }
                            placeholder="e.g. 1 or Pilot — leave blank for film"
                            maxLength={20}
                            className="h-12 mt-2 bg-secondary border-transparent text-base"
                        />
                    </div>
                    <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
                        <div>
                            <Label
                                htmlFor="add-scene-number"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Scene Number
                            </Label>
                            <Input
                                id="add-scene-number"
                                data-testid="add-scene-number-input"
                                value={form.scene_number}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        scene_number: e.target.value,
                                    }))
                                }
                                aria-invalid={isDuplicate}
                                placeholder="e.g. 3A"
                                className={`h-12 mt-2 bg-secondary border-transparent text-lg font-semibold text-center mono focus-visible:ring-1 ${
                                    isDuplicate
                                        ? "ring-1 ring-red-400 focus-visible:ring-red-400"
                                        : "focus-visible:ring-primary"
                                }`}
                            />
                        </div>
                        <div>
                            <Label
                                htmlFor="add-scene-heading"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Heading
                            </Label>
                            <Input
                                id="add-scene-heading"
                                data-testid="add-scene-heading-input"
                                value={form.heading}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, heading: e.target.value }))
                                }
                                placeholder="INT. HOUSE - DAY"
                                className="h-12 mt-2 bg-secondary border-transparent text-base"
                            />
                        </div>
                    </div>

                    {isDuplicate ? (
                        <p
                            data-testid="add-scene-duplicate-error"
                            className="flex items-center gap-2 text-xs text-red-400 -mt-3"
                        >
                            <AlertTriangle size={14} />
                            Scene number already exists.
                        </p>
                    ) : null}

                    <div>
                        <Label
                            htmlFor="add-scene-description"
                            className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Scene Description
                        </Label>
                        <Textarea
                            id="add-scene-description"
                            data-testid="add-scene-description-input"
                            value={form.description}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, description: e.target.value }))
                            }
                            rows={3}
                            maxLength={2000}
                            placeholder="Brief summary of what happens in this scene…"
                            className="mt-2 bg-secondary border-transparent text-base resize-none"
                        />
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            INT / EXT
                        </Label>
                        <div className="mt-2 flex gap-2 flex-wrap">
                            <SegmentButton
                                active={!form.int_ext}
                                testid="add-scene-intext-none"
                                onClick={() => setForm((f) => ({ ...f, int_ext: "" }))}
                            >
                                —
                            </SegmentButton>
                            {INT_EXT_OPTIONS.map((o) => (
                                <SegmentButton
                                    key={o}
                                    active={form.int_ext === o}
                                    testid={`add-scene-intext-${o.replace("/", "-")}`}
                                    onClick={() =>
                                        setForm((f) => ({ ...f, int_ext: o }))
                                    }
                                >
                                    {o}
                                </SegmentButton>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Time of day
                        </Label>
                        <div className="mt-2 flex gap-2 flex-wrap">
                            <SegmentButton
                                active={!form.time_of_day}
                                testid="add-scene-tod-none"
                                onClick={() =>
                                    setForm((f) => ({ ...f, time_of_day: "" }))
                                }
                            >
                                —
                            </SegmentButton>
                            {TIME_OF_DAY_OPTIONS.map((o) => (
                                <SegmentButton
                                    key={o}
                                    active={form.time_of_day === o}
                                    testid={`add-scene-tod-${o}`}
                                    onClick={() =>
                                        setForm((f) => ({ ...f, time_of_day: o }))
                                    }
                                >
                                    {o}
                                </SegmentButton>
                            ))}
                        </div>
                        <div className="mt-3">
                            <Label
                                htmlFor="add-scene-story-time"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Story Time
                            </Label>
                            <Input
                                id="add-scene-story-time"
                                data-testid="add-scene-story-time-input"
                                value={form.story_time}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        story_time: e.target.value,
                                    }))
                                }
                                placeholder="e.g. 7:42pm, Day 3, Two hours later"
                                maxLength={80}
                                className="h-12 mt-2 bg-secondary border-transparent text-base"
                            />
                        </div>
                    </div>

                    {error ? (
                        <p
                            data-testid="add-scene-error"
                            className="text-sm text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}

                    <div className="flex items-center gap-3 pt-1">
                        <Button
                            data-testid="add-scene-cancel"
                            type="button"
                            variant="secondary"
                            disabled={saving}
                            onClick={onClose}
                            className="flex-1 min-tap h-12"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="add-scene-save"
                            type="button"
                            disabled={!canSave}
                            onClick={save}
                            className="flex-1 min-tap h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Adding…
                                </>
                            ) : (
                                <>
                                    <Plus size={16} />
                                    Add scene
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SegmentButton({ active, onClick, testid, children }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`tap min-h-[44px] px-4 rounded-lg text-sm font-semibold border transition-colors ${
                active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-secondary text-neutral-200 border-white/10 hover:bg-white/10"
            }`}
        >
            {children}
        </button>
    );
}
